import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useGetApiCoursesInfinite } from '@edusense/course-client';
import styled from '@emotion/styled';
const CourseListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
`;
const CourseCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;
const CourseTitle = styled.h3`
  margin: 0 0 10px 0;
  color: #333;
`;
const CourseDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;
const LoadingIndicator = styled.div`
  text-align: center;
  padding: 20px;
  color: #999;
`;
const ErrorMessage = styled.div`
  color: #ff4444;
  padding: 20px;
  text-align: center;
  background-color: #ffeeee;
  border-radius: 8px;
`;
const LoadMoreTrigger = styled.div`
  height: 20px;
  margin: 20px 0;
`;
export const CourseListInfinite = ({ type = GetApiCoursesType.FORYOU, pageSize = 20, sort = [
    'createdDate,desc'
] })=>{
    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0.1,
        triggerOnce: false
    });
    // Orval이 생성한 infinite scroll 훅 사용
    const { data, error, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } = useGetApiCoursesInfinite({
        // API 파라미터들
        type,
        page: 0,
        size: pageSize,
        sort
    }, {
        // React Query 옵션
        query: {
            // 다음 페이지 파라미터 계산
            getNextPageParam: (lastPage)=>{
                // 마지막 페이지 확인
                if (lastPage.data?.last) {
                    return undefined;
                }
                // 다음 페이지 번호 반환
                const currentPage = lastPage.data?.number ?? 0;
                return currentPage + 1;
            },
            // 이전 페이지 파라미터 (필요시)
            getPreviousPageParam: (firstPage)=>{
                const currentPage = firstPage.data?.number ?? 0;
                if (currentPage === 0) {
                    return undefined;
                }
                return currentPage - 1;
            },
            // 초기 페이지 파라미터
            initialPageParam: 0
        }
    });
    // 스크롤이 하단에 도달하면 다음 페이지 로드
    useEffect(()=>{
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [
        inView,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(LoadingIndicator, {
            children: "코스 목록을 불러오는 중..."
        });
    }
    if (isError) {
        return /*#__PURE__*/ _jsxs(ErrorMessage, {
            children: [
                "코스 목록을 불러오는 중 오류가 발생했습니다.",
                error?.message && /*#__PURE__*/ _jsx("div", {
                    children: error.message
                })
            ]
        });
    }
    // 모든 페이지의 코스들을 하나의 배열로 합치기
    const allCourses = data?.pages.flatMap((page)=>page.data?.content ?? []) ?? [];
    return /*#__PURE__*/ _jsxs(CourseListContainer, {
        children: [
            allCourses.map((course)=>/*#__PURE__*/ _jsxs(CourseCard, {
                    children: [
                        /*#__PURE__*/ _jsx(CourseTitle, {
                            children: course.title
                        }),
                        /*#__PURE__*/ _jsx(CourseDescription, {
                            children: course.description
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("strong", {
                                    children: "강사:"
                                }),
                                " ",
                                course.instructor?.name ?? '정보 없음'
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("strong", {
                                    children: "가격:"
                                }),
                                " ",
                                course.price?.amount ?? 0,
                                " ",
                                course.price?.currency ?? 'KRW'
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("strong", {
                                    children: "섹션 수:"
                                }),
                                " ",
                                course.sections?.length ?? 0,
                                "개"
                            ]
                        })
                    ]
                }, course.courseId)),
            /*#__PURE__*/ _jsxs(LoadMoreTrigger, {
                ref: loadMoreRef,
                children: [
                    isFetchingNextPage && /*#__PURE__*/ _jsx(LoadingIndicator, {
                        children: "추가 코스를 불러오는 중..."
                    }),
                    !hasNextPage && allCourses.length > 0 && /*#__PURE__*/ _jsx(LoadingIndicator, {
                        children: "모든 코스를 불러왔습니다."
                    })
                ]
            })
        ]
    });
};
export default CourseListInfinite;
